import { motion } from 'framer-motion';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { theme } from '../../theme';
import { useMobile } from '../../hooks/useMediaQuery';

interface SpendingChartProps {
  data: any[];
  type?: 'line' | 'bar' | 'pie';
  height?: number;
  title?: string;
}

const COLORS = [...theme.chartColors];
const gridStroke = theme.semantic.bg.elevated;

const axisProps = {
  stroke: theme.semantic.text.disabled,
  fontSize: 11,
  fontFamily: theme.typography.fontFamily.mono,
  tickLine: false,
  axisLine: false,
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: theme.semantic.bg.surface,
      border: `${theme.borders.medium} ${theme.semantic.border.default}`,
      padding: theme.spacing[3],
    }}>
      {label && (
        <div style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: theme.typography.fontSize.xs,
          color: theme.semantic.text.muted,
          letterSpacing: theme.typography.letterSpacing.wider,
          textTransform: 'uppercase' as const,
          marginBottom: theme.spacing[1],
        }}>
          {label}
        </div>
      )}
      {payload.map((entry: any, i: number) => (
        <div key={i} style={{
          fontFamily: theme.typography.fontFamily.mono,
          fontSize: theme.typography.fontSize.sm,
          color: entry.color,
        }}>
          {entry.name}: £{Number(entry.value).toLocaleString()}
        </div>
      ))}
    </div>
  );
};

export const SpendingChart = ({
  data, type = 'line', height = 260, title,
}: SpendingChartProps) => {
  const mobile = useMobile();
  const chartHeight = mobile ? 200 : height;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        background: theme.semantic.bg.surface,
        borderTop: `${theme.borders.heavy} ${theme.brand.crimson}`,
        padding: mobile ? theme.spacing[3] : theme.spacing[5],
      }}
    >
      {title && (
        <h3 style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: theme.typography.fontSize.sm,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.semantic.text.muted,
          letterSpacing: theme.typography.letterSpacing.widest,
          textTransform: 'uppercase' as const,
          marginBottom: theme.spacing[4],
        }}>
          {title}
        </h3>
      )}

      <ResponsiveContainer width="100%" height={chartHeight}>
        {type === 'line' ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone" dataKey="spending"
              stroke={theme.brand.crimson} strokeWidth={2}
              dot={{ fill: theme.brand.crimson, r: 3, strokeWidth: 0 }}
            />
            <Line
              type="monotone" dataKey="budget"
              stroke={theme.colors.success} strokeWidth={2}
              strokeDasharray="6 4" dot={false}
            />
          </LineChart>
        ) : type === 'bar' ? (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="amount" radius={0}>
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        ) : (
          <PieChart>
            <Pie
              data={data} cx="50%" cy="50%"
              outerRadius={mobile ? 65 : 85} innerRadius={mobile ? 30 : 40}
              paddingAngle={1} dataKey="value" strokeWidth={0}
              label={mobile ? false : ({ name, percent }: any) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </motion.div>
  );
};

export default SpendingChart;
